"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { Checkbox, Input, Label, Textarea } from "@relume_io/relume-ui";
import { LandingReveal } from "@/components/landing/motion/LandingReveal";
import { LandingRevealGroup } from "@/components/landing/motion/LandingRevealGroup";
import TrackedPhoneLink from "@/components/ui/TrackedPhoneLink";
import { trackGoogleAdsContactConversion } from "@/lib/google-ads";

type ApiResponse = { ok: boolean; error?: string };

function isValidEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function isValidPhone(value: string): boolean {
    const digits = value.replace(/\D/g, "");
    return digits.length >= 9 && digits.length <= 15;
}

function hasValidContact(phone: string, email: string): boolean {
    const phoneTrimmed = phone.trim();
    const emailTrimmed = email.trim();
    const phoneProvided = phoneTrimmed.length > 0;
    const emailProvided = emailTrimmed.length > 0;

    if (!phoneProvided && !emailProvided) return false;
    if (phoneProvided && !isValidPhone(phoneTrimmed)) return false;
    if (emailProvided && !isValidEmail(emailTrimmed)) return false;
    return true;
}

const formShellClassName =
    "rounded-[2rem] border border-secondary/10 bg-primary-light p-6 md:rounded-[2.5rem] md:p-7";

const fieldGroupClassName = "pt-1 first:pt-0";

const centeredTextFieldClassName =
    "h-10 w-full rounded-2xl border border-secondary/10 bg-white/50 px-2.5 text-base leading-[2.5rem] text-secondary shadow-none placeholder:text-secondary/40";

const messageFieldClassName =
    "min-h-[7.5rem] w-full rounded-2xl border border-secondary/10 bg-white/50 px-2.5 py-2.5 text-base text-secondary shadow-none placeholder:text-secondary/40";

export function CTAFormFields() {
    const [hasMounted, setHasMounted] = useState(false);
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [email, setEmail] = useState("");
    const [message, setMessage] = useState("");
    const [acceptTerms, setAcceptTerms] = useState<boolean | "indeterminate">(false);
    const [website, setWebsite] = useState("");
    const [formStartedAt, setFormStartedAt] = useState(() => Date.now());
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        setHasMounted(true);
    }, []);

    const canSubmit = useMemo(() => {
        const termsOk = acceptTerms === true;
        const contactOk = hasValidContact(phone, email);
        return contactOk && termsOk && !isSubmitting;
    }, [phone, acceptTerms, email, isSubmitting]);

    const resetForm = () => {
        setName("");
        setPhone("");
        setEmail("");
        setMessage("");
        setAcceptTerms(false);
        setWebsite("");
        setFormStartedAt(Date.now());
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setErrorMessage(null);

        const nameTrimmed = name.trim();
        const phoneTrimmed = phone.trim();
        const emailTrimmed = email.trim();
        const messageTrimmed = message.trim();
        const termsOk = acceptTerms === true;
        const phoneProvided = phoneTrimmed.length > 0;
        const emailProvided = emailTrimmed.length > 0;

        if (!termsOk) {
            setErrorMessage("Zaakceptuj politykę prywatności.");
            return;
        }

        if (!phoneProvided && !emailProvided) {
            setErrorMessage("Podaj numer telefonu lub adres e-mail — wystarczy jedno z pól.");
            return;
        }

        if (phoneProvided && !isValidPhone(phoneTrimmed)) {
            setErrorMessage("Podaj poprawny numer telefonu.");
            return;
        }

        if (emailProvided && !isValidEmail(emailTrimmed)) {
            setErrorMessage("Podaj poprawny adres e-mail.");
            return;
        }

        if (messageTrimmed.length > 5000) {
            setErrorMessage("Wiadomość jest zbyt długa.");
            return;
        }

        if (Date.now() - formStartedAt < 1500) {
            setErrorMessage("Wypełnij formularz i spróbuj ponownie.");
            return;
        }

        setIsSubmitting(true);

        try {
            const res = await fetch("/api/contact", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                cache: "no-store",
                body: JSON.stringify({
                    formType: "cta",
                    name: nameTrimmed || undefined,
                    phone: phoneTrimmed || undefined,
                    email: emailTrimmed || undefined,
                    message: messageTrimmed || undefined,
                    acceptTerms: true,
                    website,
                    formStartedAt,
                }),
            });

            const data = (await res.json().catch(() => null)) as ApiResponse | null;

            if (!res.ok || !data?.ok) {
                setErrorMessage(data?.error ?? "Nie udało się wysłać zapytania. Spróbuj ponownie.");
                return;
            }

            resetForm();
            setIsSuccess(true);
            trackGoogleAdsContactConversion();
        } catch {
            setErrorMessage("Błąd sieci. Spróbuj ponownie za chwilę.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!hasMounted) {
        return <div className={formShellClassName} aria-hidden="true" />;
    }

    if (isSuccess) {
        return (
            <div className={`${formShellClassName} text-center`} role="status" aria-live="polite">
                <p className="text-sm font-medium text-secondary md:text-base">
                    Dziękujemy! Odezwiemy się jak najszybciej.
                </p>
            </div>
        );
    }

    return (
        <>
            <div className={formShellClassName}>
                <form className="grid grid-cols-1 gap-4" onSubmit={handleSubmit} noValidate>
                    <div
                        aria-hidden="true"
                        className="absolute left-[-10000px] h-px w-px overflow-hidden"
                    >
                        <label htmlFor="cta-form-website">Website</label>
                        <input
                            id="cta-form-website"
                            name="website"
                            type="text"
                            tabIndex={-1}
                            autoComplete="off"
                            value={website}
                            onChange={(e) => setWebsite(e.target.value)}
                        />
                    </div>

                    <LandingRevealGroup className="flex flex-col gap-2 md:gap-3">
                        <LandingReveal custom={0} className={`${fieldGroupClassName} will-change-transform`}>
                            <Input
                                type="text"
                                id="ctaFormName"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Nazwa firmy"
                                maxLength={120}
                                autoComplete="organization"
                                className={centeredTextFieldClassName}
                            />
                        </LandingReveal>

                        <LandingReveal custom={1} className={`${fieldGroupClassName} will-change-transform`}>
                            <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                                <Input
                                    type="tel"
                                    id="ctaFormPhone"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    placeholder="Telefon"
                                    maxLength={20}
                                    autoComplete="tel"
                                    inputMode="tel"
                                    className={centeredTextFieldClassName}
                                />
                                <Input
                                    type="email"
                                    id="ctaFormEmail"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="E-mail"
                                    maxLength={254}
                                    autoComplete="email"
                                    inputMode="email"
                                    className={centeredTextFieldClassName}
                                />
                            </div>
                        </LandingReveal>

                        <LandingReveal custom={2} className={`${fieldGroupClassName} will-change-transform`}>
                            <Textarea
                                id="ctaFormMessage"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="Wiadomość"
                                maxLength={5000}
                                className={messageFieldClassName}
                            />
                        </LandingReveal>

                        <LandingReveal custom={3} className="pt-4 md:pt-5 will-change-transform">
                            <div className="flex flex-col items-stretch gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
                                <div className="flex items-center gap-2.5">
                                    <Checkbox
                                        id="ctaFormTerms"
                                        checked={acceptTerms}
                                        onCheckedChange={setAcceptTerms}
                                        required
                                        className="flex size-5 shrink-0 items-center justify-center rounded-full border-2 border-secondary/60 p-0 text-white data-[state=checked]:border-accent data-[state=checked]:bg-accent data-[state=checked]:text-white md:size-4 [&>span]:flex [&>span]:size-full [&>span]:items-center [&>span]:justify-center [&_svg]:size-3.5 md:[&_svg]:size-3"
                                    />
                                    <Label
                                        htmlFor="ctaFormTerms"
                                        className="cursor-pointer text-xs leading-tight text-secondary/75"
                                    >
                                        Akceptuję{" "}
                                        <Link
                                            href="/polityka-prywatnosci-i-pliki-cookies"
                                            className="text-accent underline underline-offset-2"
                                        >
                                            politykę prywatności
                                        </Link>{" "}
                                        i zgadzam się na kontakt w sprawie zapytania.
                                    </Label>
                                </div>
                                <button
                                    type="submit"
                                    disabled={!canSubmit}
                                    className="inline-flex w-full shrink-0 items-center justify-center rounded-full bg-accent px-6 py-2.5 text-xs font-medium uppercase tracking-wide text-white transition hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-60 md:w-auto"
                                >
                                    {isSubmitting ? "Wysyłanie..." : "WYŚLIJ ZAPYTANIE"}
                                </button>
                            </div>
                        </LandingReveal>

                        {errorMessage ? (
                            <p
                                className="rounded-2xl bg-red-500/10 px-3 py-2 text-xs text-red-700"
                                role="alert"
                            >
                                {errorMessage}
                            </p>
                        ) : null}
                    </LandingRevealGroup>
                </form>
            </div>
            <div className="mt-4 text-center text-xs leading-relaxed text-secondary/70">
                <p>
                    <TrackedPhoneLink
                        href="tel:+48732493079"
                        className="font-medium text-secondary underline underline-offset-2 hover:text-accent"
                    >
                        732 493 079
                    </TrackedPhoneLink>{" "}
                    lub{" "}
                    <TrackedPhoneLink
                        href="tel:+48732493080"
                        className="font-medium text-secondary underline underline-offset-2 hover:text-accent"
                    >
                        732 493 080
                    </TrackedPhoneLink>
                </p>
            </div>
        </>
    );
}
